// A profile's UI editions (RFC-062 D1–D3): pages, a frame, routes and words
// of their own, worn by a third party's candidates and operators (/v1/me's
// `integration`) where the profile names one (Profile.UI, `ui`); everyone
// else is shown Share's own pages. Each edition is a chunk of its own,
// loaded once it is named, so Share's bundle carries none of its pages and
// it none of Share's. A fork adds its own here, beside these.
import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from "react";
import type { Product, User } from "../api";
import { PageLoading } from "../components/Page";

/** What App gives an edition: who is signed in, where they are, and how they sign out. */
export interface EditionAppProps {
  user: User;
  path: string;
  query: URLSearchParams;
  onSignOut: () => void;
}

/** What the login page gives an edition's signed-out page: the product, and where to sign in again. */
export interface SignedOutProps {
  product?: Product;
  signinURL?: string;
}

/** An edition: its app — frame, routes and pages — and its page for one signed out. */
export interface Edition {
  App: ComponentType<EditionAppProps>;
  SignedOut: ComponentType<SignedOutProps>;
}

const editions: Record<string, () => Promise<Edition>> = {
  exam: () => import("./exam").then((m) => m.edition),
};

/** Whether name is an edition this web app has. */
export function hasEdition(name?: string): name is string {
  return !!name && Object.hasOwn(editions, name);
}

/** Whether the person wears their profile's edition: a third party's candidate or operator, where it names one. */
export function wearsEdition(user: Pick<User, "ui" | "integration">): boolean {
  return !!user.integration && hasEdition(user.ui);
}

const apps = new Map<string, LazyExoticComponent<ComponentType<EditionAppProps>>>();
const outs = new Map<string, LazyExoticComponent<ComponentType<SignedOutProps>>>();

/** The edition named, signed in: its frame and the page for where they are. */
export function EditionApp({ name, ...props }: EditionAppProps & { name: string }) {
  let App = apps.get(name);
  if (!App) apps.set(name, (App = lazy(() => editions[name]!().then((e) => ({ default: e.App })))));
  return (
    <Suspense fallback={<PageLoading />}>
      <App {...props} />
    </Suspense>
  );
}

/** The edition named, signed out: where its person goes to sign in again, never Share's login. */
export function EditionSignedOut({ name, ...props }: SignedOutProps & { name: string }) {
  let Out = outs.get(name);
  if (!Out) outs.set(name, (Out = lazy(() => editions[name]!().then((e) => ({ default: e.SignedOut })))));
  return (
    <Suspense fallback={<PageLoading />}>
      <Out {...props} />
    </Suspense>
  );
}
